/**
 * Email Extraction Utility
 * 
 * Responsible for extracting email addresses from different Outlook item types.
 * Follows Single Responsibility Principle and Open/Closed Principle using Strategy pattern.
 */

/* global Office */

/**
 * Base strategy for extracting emails from Outlook items
 */
class EmailExtractionStrategy {
  async extract(item, loggedInEmail) {
    throw new Error('extract() must be implemented by subclass');
  }
}

/**
 * Strategy for extracting emails from regular email messages
 */
class MessageEmailExtractor extends EmailExtractionStrategy {
  async extract(item, loggedInEmail) {
    const senderEmail = item.from?.emailAddress;
    const toRecipients = item.to;
    const ccRecipients = item.cc;

    const allEmails = [
      senderEmail,
      ...(toRecipients ? toRecipients.map(recipient => recipient.emailAddress) : []),
      ...(ccRecipients ? ccRecipients.map(recipient => recipient.emailAddress) : [])
    ].filter(email => email && email !== loggedInEmail);

    return allEmails;
  }
}

/**
 * Strategy for extracting emails from appointments/meetings
 */
class AppointmentEmailExtractor extends EmailExtractionStrategy {
  async extract(item, loggedInEmail) {
    const attendeesData = await this._getAttendeesData(item);
    const emails = this._extractEmailsFromAttendees(attendeesData);
    
    return emails.filter(email => email && email !== loggedInEmail);
  }

  /**
   * Get attendees data from appointment item
   * Handles both read and edit modes
   */
  async _getAttendeesData(item) {
    let requiredAttendees = [];
    let optionalAttendees = [];
    let organizer = null;

    try {
      // Try synchronous access first (read mode)
      requiredAttendees = await this._getRequiredAttendees(item);
      optionalAttendees = await this._getOptionalAttendees(item);
      organizer = await this._getOrganizer(item);
    } catch (error) {
      console.error('Attendee retrieval failed:', error);
    }

    // If synchronous access failed, try async methods (edit mode)
    if (requiredAttendees.length === 0 && optionalAttendees.length === 0 && !organizer) {
      const [requiredResult, optionalResult, organizerResult] = await Promise.allSettled([
        this._getAttendeesAsync(item.requiredAttendees),
        this._getAttendeesAsync(item.optionalAttendees),
        this._getOrganizerAsync(item.organizer)
      ]);

      // Only update values if they're still empty
      if (requiredAttendees.length === 0) {
        requiredAttendees = requiredResult.status === 'fulfilled' ? requiredResult.value : [];
      }
      if (optionalAttendees.length === 0) {
        optionalAttendees = optionalResult.status === 'fulfilled' ? optionalResult.value : [];
      }
      if (!organizer) {
        organizer = organizerResult.status === 'fulfilled' ? organizerResult.value : null;
      }
    }

    return { requiredAttendees, optionalAttendees, organizer };
  }

  /**
   * Get required attendees (handles both sync and async access)
   */
  async _getRequiredAttendees(item) {
    return new Promise((resolve) => {
      if (item.requiredAttendees && typeof item.requiredAttendees.getAsync === 'function') {
        item.requiredAttendees.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            resolve([]);
          }
        });
      } else if (Array.isArray(item.requiredAttendees)) {
        resolve(item.requiredAttendees);
      } else {
        resolve([]);
      }
    });
  }

  /**
   * Get optional attendees (handles both sync and async access)
   */
  async _getOptionalAttendees(item) {
    return new Promise((resolve) => {
      if (item.optionalAttendees && typeof item.optionalAttendees.getAsync === 'function') {
        item.optionalAttendees.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            resolve([]);
          }
        });
      } else if (Array.isArray(item.optionalAttendees)) {
        resolve(item.optionalAttendees);
      } else {
        resolve([]);
      }
    });
  }

  /**
   * Get organizer (handles both sync and async access)
   */
  async _getOrganizer(item) {
    return new Promise((resolve) => {
      if (item.organizer && typeof item.organizer.getAsync === 'function') {
        item.organizer.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            resolve(null);
          }
        });
      } else if (item.organizer) {
        resolve(item.organizer);
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Get attendees asynchronously
   */
  _getAttendeesAsync(attendeesProperty) {
    return new Promise((resolve) => {
      if (attendeesProperty && typeof attendeesProperty.getAsync === 'function') {
        attendeesProperty.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : []);
        });
      } else {
        resolve([]);
      }
    });
  }

  /**
   * Get organizer asynchronously
   */
  _getOrganizerAsync(organizerProperty) {
    return new Promise((resolve) => {
      if (organizerProperty && typeof organizerProperty.getAsync === 'function') {
        organizerProperty.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
        });
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Extract email addresses from attendees data
   */
  _extractEmailsFromAttendees({ requiredAttendees, optionalAttendees, organizer }) {
    const requiredEmails = this._extractEmailsFromList(requiredAttendees);
    const optionalEmails = this._extractEmailsFromList(optionalAttendees);
    const organizerEmail = this._extractEmail(organizer);

    return [
      organizerEmail,
      ...requiredEmails,
      ...optionalEmails
    ].filter(email => email);
  }

  /**
   * Extract emails from a list of attendees
   */
  _extractEmailsFromList(attendees) {
    return attendees
      .map(attendee => this._extractEmail(attendee))
      .filter(email => email);
  }

  /**
   * Extract email from a single attendee object
   */
  _extractEmail(attendee) {
    if (!attendee) return null;
    const email = attendee?.emailAddress || attendee?.address || attendee?.email;
    return typeof email === 'string' ? email : null;
  }
}

/**
 * Factory for creating email extraction strategies
 */
class EmailExtractorFactory {
  static create(itemType) {
    if (itemType === Office.MailboxEnums.ItemType.Appointment) {
      return new AppointmentEmailExtractor();
    }
    return new MessageEmailExtractor();
  }
}

/**
 * Main service for email extraction
 * Follows Open/Closed Principle - extensible without modification
 */
export class EmailExtractionService {
  /**
   * Extract all relevant email addresses from an Outlook item
   * 
   * @param {Object} item - Outlook item (email or appointment)
   * @param {string} loggedInEmail - Email of logged-in user (to exclude)
   * @returns {Promise<string[]>} Array of email addresses
   */
  async extractEmails(item, loggedInEmail) {
    const strategy = EmailExtractorFactory.create(item.itemType);
    return await strategy.extract(item, loggedInEmail);
  }
}

